import axios from 'axios';
import {
  isCurrentTokenExpired,
  saveCurrentPathAndRedirectToLoginWithHref,
} from '../utils/authUtils';

const getApiUrl = () => {
  if (typeof window !== 'undefined') {
    const { protocol, hostname, origin } = window.location;
    const isAwsIpHost = /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname);
    const isDynamicSandboxHost = hostname.endsWith('.sslip.io');
    const isSandboxHost =
      hostname === 'sandbox.postpop.kr' ||
      hostname === 'postpop-sandbox.kro.kr' ||
      hostname === 'test-dudu.kro.kr';

    if (
      (protocol === 'http:' || protocol === 'https:') &&
      (isAwsIpHost || isDynamicSandboxHost || isSandboxHost)
    ) {
      return `${origin}/api`;
    }
  }

  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }

  const env = process.env.REACT_APP_ENVIRONMENT || 'LOCAL';

  switch (env) {
    case 'PRODUCTION':
      return 'https://postpop.kr/api';
    case 'SANDBOX':
      return 'https://test-dudu.kro.kr/api';
    default:
      return 'http://localhost:8080';
  }
};

const api = axios.create({
  baseURL: getApiUrl(),
});

// 전역 에러 핸들러 함수 (나중에 ErrorContext에서 설정됨)
let globalErrorHandler = null;

// 전역 에러 핸들러 설정 함수
api.setGlobalErrorHandler = (handler) => {
  globalErrorHandler = handler;
};

// 요청 인터셉터
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('POSTPOP_AUTH_TOKEN');
    if (config.requireAuth === true) {
      if (token) {
        // 토큰이 만료되었는지 미리 확인
        if (isCurrentTokenExpired()) {
          if (api.onUnauthorized) {
            try {
              api.onUnauthorized();
            } catch (err) {
              console.error('onUnauthorized 함수 실행 중 오류:', err);
            }
          } else {
            // onUnauthorized 함수가 설정되지 않은 경우 기본 동작
            localStorage.removeItem('POSTPOP_AUTH_TOKEN');
            saveCurrentPathAndRedirectToLoginWithHref();
          }
          // 요청을 중단하고 에러를 반환
          return Promise.reject(new Error('로그인 유지 시간이 만료되어\n자동으로 로그아웃되었습니다.'));
        }
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    if (config.requireAuth === false) {
      delete config.headers.Authorization;
    }
    config.headers['X-Page-URL'] = window.location.href;
    return config;
  },
  (error) => Promise.reject(error),
);

// 응답 인터셉터 추가
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const originalRequest = error.config;

    // API 응답에서 에러 메시지 추출
    if (error.response && error.response.data) {
      const { data } = error.response;

      // 에러 응답 구조에 따라 메시지 추출
      if (data.error && data.error.message) {
        error.message = data.error.message;
      } else if (data.msg) {
        error.message = data.msg;
      } else if (typeof data === 'string') {
        error.message = data;
      } else {
        error.message = '알 수 없는 오류가 발생했습니다.';
      }
    } else if (error.message) {
      // 네트워크 오류 등
      if (error.code === 'NETWORK_ERROR') {
        error.message = '네트워크 연결을 확인해주세요.';
      } else if (error.code === 'ECONNABORTED') {
        error.message = '요청 시간이 초과되었습니다.';
      }
    } else {
      error.message = '알 수 없는 오류가 발생했습니다.';
    }

    // 401 에러 처리 (인증 실패) - 모든 401 에러에 대해 로그인 페이지로 리다이렉트
    if (error.response?.status === 401) {
      console.log('401 에러 발생 - 로그인 페이지로 리다이렉트');
      console.log('api.onUnauthorized 함수 존재 여부:', !!api.onUnauthorized);

      if (api.onUnauthorized) {
        try {
          api.onUnauthorized();
        } catch (err) {
          console.error('onUnauthorized 함수 실행 중 오류:', err);
        }
      } else {
        // onUnauthorized 함수가 설정되지 않은 경우 기본 동작
        localStorage.removeItem('POSTPOP_AUTH_TOKEN');
        saveCurrentPathAndRedirectToLoginWithHref();
      }
      // 401 에러는 전역 에러로 표시하지 않음 (로그인 페이지로 이동하므로)
      return Promise.reject(error);
    }

    // 전역 에러 핸들러가 설정되어 있고, suppressGlobalError가 false인 경우에만 전역 에러 표시
    if (globalErrorHandler && !originalRequest?.suppressGlobalError) {
      globalErrorHandler(error.message, 'error');
    }

    return Promise.reject(error);
  },
);

api.getAuth = (url, config = {}) => api.get(url, { ...config, requireAuth: true });

api.postAuth = (url, data, config = {}) => api.post(url, data, { ...config, requireAuth: true });

api.putAuth = (url, data, config = {}) => api.put(url, data, { ...config, requireAuth: true });

api.deleteAuth = (url, config = {}) => api.delete(url, { ...config, requireAuth: true });

api.getPublic = (url, config = {}) => api.get(url, { ...config, requireAuth: false });

api.postPublic = (url, data, config = {}) => api.post(url, data, { ...config, requireAuth: false });

api.putPublic = (url, data, config = {}) => api.put(url, data, { ...config, requireAuth: false });

api.deletePublic = (url, config = {}) => api.delete(url, { ...config, requireAuth: false });

export default api;
